import { DateTime } from 'luxon'
import { buildDateTime } from 'lib/CustomLuxonUtils'
import { splitRecurrenceOccurrences } from './makeDefaultValues'

export const IMPACT_TYPE_DATE_CANCELLATION = 'date_cancellation'
export const IMPACT_TYPE_LOCATION_CHANGE = 'location_change'
export const IMPACT_TYPE_SCHEDULE_CHANGE = 'schedule_change'

const toUtcIso = dateTime =>
  dateTime?.isValid
    ? dateTime.toUTC().toFormat("yyyy-MM-dd'T'HH:mm:ss'Z'")
    : null

const isSameDateTime = (a, b) => toUtcIso(a) === toUtcIso(b)

const getRegistrants = occurrence => occurrence?.registrations ?? []

const buildOccurrenceMap = event =>
  new Map(
    (event?.recurrence?.groupEvents ?? []).map(occurrence => [
      occurrence.id,
      occurrence,
    ]),
  )

export const formatImpactType = types => {
  const labels = []

  if (types.includes(IMPACT_TYPE_DATE_CANCELLATION)) {
    labels.push('canceled')
  }
  if (types.includes(IMPACT_TYPE_SCHEDULE_CHANGE)) {
    labels.push('schedule changed')
  }
  if (types.includes(IMPACT_TYPE_LOCATION_CHANGE)) {
    labels.push('location changed')
  }

  if (labels.length === 1) return labels[0]
  if (labels.length === 2) return labels.join(' and ')
  return `${labels.slice(0, -1).join(', ')}, and ${labels.slice(-1)}`
}

const getRecurringImpacts = (event, formValues) => {
  const impacts = []
  const occurrenceMap = buildOccurrenceMap(event)
  const { sessions: upcomingSessions } = splitRecurrenceOccurrences(event)
  const upcomingIds = upcomingSessions.map(session => session.groupEventId)
  const remainingIds = new Set(
    formValues.sessions.map(session => session.groupEventId).filter(Boolean),
  )

  upcomingIds
    .filter(id => !remainingIds.has(id))
    .forEach(id => {
      const occurrence = occurrenceMap.get(id)
      const registrants = getRegistrants(occurrence)
      if (registrants.length > 0) {
        impacts.push({
          type: [IMPACT_TYPE_DATE_CANCELLATION],
          occurrence,
          registrants,
        })
      }
    })

  const locationChanged = formValues.locationId !== event?.coachFacility?.id

  formValues.sessions.forEach(session => {
    const occurrence = occurrenceMap.get(session.groupEventId)
    if (!occurrence) return

    const newStart = buildDateTime(
      session.startDate,
      session.startTime,
      event.timezone,
    )
    const newEnd = buildDateTime(
      session.startDate,
      session.endTime,
      event.timezone,
    )
    const oldStart = DateTime.fromISO(occurrence.startDateTime)
    const oldEnd = DateTime.fromISO(occurrence.endDateTime)

    const scheduleChanged =
      !isSameDateTime(newStart, oldStart) || !isSameDateTime(newEnd, oldEnd)

    if (!scheduleChanged && !locationChanged) return

    const registrants = getRegistrants(occurrence)
    if (registrants.length === 0) return

    const types = []
    if (scheduleChanged) types.push(IMPACT_TYPE_SCHEDULE_CHANGE)
    if (locationChanged) types.push(IMPACT_TYPE_LOCATION_CHANGE)

    impacts.push({
      type: types,
      occurrence,
      registrants,
    })
  })

  return impacts
}

const buildDateTimeSignature = (session, timezone) => {
  const start = buildDateTime(session.startDate, session.startTime, timezone)
  const end = buildDateTime(session.startDate, session.endTime, timezone)
  return `${toUtcIso(start)}:${toUtcIso(end)}`
}

const getSingleEventImpacts = (event, formValues) => {
  const impacts = []
  const registrants = getRegistrants(event)
  if (registrants.length === 0) return impacts

  const locationChanged = formValues.locationId !== event?.coachFacility?.id

  const originalDateTimeSignatures = new Set(
    (event?.sessions ?? []).map(
      session =>
        `${toUtcIso(DateTime.fromISO(session.startDateTime))}:${toUtcIso(
          DateTime.fromISO(session.endDateTime),
        )}`,
    ),
  )

  const newDateTimeSignatures = new Set(
    formValues.sessions.map(session =>
      buildDateTimeSignature(session, event.timezone),
    ),
  )

  const sessionsChanged =
    originalDateTimeSignatures.size !== newDateTimeSignatures.size ||
    [...originalDateTimeSignatures].some(
      signature => !newDateTimeSignatures.has(signature),
    )

  const types = []
  if (sessionsChanged) types.push(IMPACT_TYPE_SCHEDULE_CHANGE)
  if (locationChanged) types.push(IMPACT_TYPE_LOCATION_CHANGE)

  if (types.length === 0) return impacts

  impacts.push({
    type: types,
    registrants,
  })

  return impacts
}

export const getUpdateImpact = ({ event, formValues, editRecurrence }) => {
  const impacts = editRecurrence
    ? getRecurringImpacts(event, formValues)
    : getSingleEventImpacts(event, formValues)

  return {
    hasImpact: impacts.length > 0,
    impacts,
  }
}

export default getUpdateImpact
